import type { Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { AssessmentTemplateStatus, AssessmentType, Grade } from './enums'

import { useSidebarPrivileges } from '@entities/viewer'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplatesApi } from '../../api/assessment-templates/list.hook'
import {
  assessmentTypeOptions,
  disciplineCodeOptions,
  gradeOptions,
  templateStatusOptions,
} from './assessment-template-options'

interface FiltersAssessmentTemplatesReturn {
  refSearch: Ref<string>
  refType: Ref<AssessmentType | undefined>
  refDisciplineCode: Ref<DisciplineCode | undefined>
  refGrade: Ref<Grade | undefined>
  refStatus: Ref<AssessmentTemplateStatus | undefined>
  typeOptions: AppSelectOption<AssessmentType>[]
  disciplineOptions: AppSelectOption<DisciplineCode>[]
  gradeSelectOptions: AppSelectOption<Grade>[]
  statusOptions: AppSelectOption<AssessmentTemplateStatus>[]
  canManage: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  submitSearch: () => Promise<void>
  openCreateModal: () => void
}

/** The controls hold a draft; nothing is requested until "Search" or Enter in the search input. */
export const useFiltersAssessmentTemplates = (): FiltersAssessmentTemplatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useAssessmentTemplatesStore()
  const { canManageAssessmentTemplates } = useSidebarPrivileges()

  const refSearch = ref<string>('')
  const refType = ref<AssessmentType | undefined>(undefined)
  const refDisciplineCode = ref<DisciplineCode | undefined>(undefined)
  const refGrade = ref<Grade | undefined>(undefined)
  const refStatus = ref<AssessmentTemplateStatus | undefined>(undefined)

  const syncDraft = (): void => {
    refSearch.value = store.getFilterSearch ?? ''
    refType.value = store.getFilterType ?? undefined
    refDisciplineCode.value = store.getFilterDisciplineCode ?? undefined
    refGrade.value = store.getFilterGrade ?? undefined
    refStatus.value = store.getFilterStatus ?? undefined
  }

  /**
   * The query is the source of truth on reload (the `assessments` middleware maps it back), so a
   * filtered list is shareable and survives a refresh. `page` is dropped so a new filter never
   * lands on a page that no longer exists.
   */
  const submitSearch = async (): Promise<void> => {
    store.setFilterSearch(refSearch.value.trim() || null)
    store.setFilterType(refType.value ?? null)
    store.setFilterDisciplineCode(refDisciplineCode.value ?? null)
    store.setFilterGrade(refGrade.value ?? null)
    store.setFilterStatus(refStatus.value ?? null)

    await router.push({
      query: {
        ...route.query,
        search: store.getFilterSearch || undefined,
        type: store.getFilterType || undefined,
        discipline_code: store.getFilterDisciplineCode || undefined,
        grade: store.getFilterGrade || undefined,
        status: store.getFilterStatus || undefined,
        page: undefined,
      },
    })
    await useAssessmentTemplatesApi()
  }

  const canManage = computed<boolean>(() => canManageAssessmentTemplates.value)
  const isLoading = computed<boolean>(() => store.isLoading)

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  // The store, not the URL: the list hydrates it from the query, and the draft follows once it has.
  watch(
    () => [
      store.getFilterSearch,
      store.getFilterType,
      store.getFilterDisciplineCode,
      store.getFilterGrade,
      store.getFilterStatus,
    ],
    syncDraft,
    { immediate: true, deep: true },
  )

  return {
    refSearch,
    refType,
    refDisciplineCode,
    refGrade,
    refStatus,
    typeOptions: assessmentTypeOptions,
    disciplineOptions: disciplineCodeOptions,
    gradeSelectOptions: gradeOptions,
    statusOptions: templateStatusOptions,
    canManage,
    isLoading,
    submitSearch,
    openCreateModal,
  }
}
